import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Input, InputNumber, Spin, Popconfirm } from "antd";
import {
  fetchQualifications,
  addQualification,
  updateQualification,
  deleteQualification,
  handleNextStep,
  handlePrevStep,
} from "../../../features/profile/profileSlice";
import { showToastError, showToastSuccess } from "../../../utils";
import toast from "react-hot-toast";
import eduImg from "../../../assets/edit_edu.png";

function closeModal() {
  toast.dismiss();
}

export default function ProfessionalQualification() {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const formRef = useRef(null);

  const { profileLoading, qualifications = [] } = useSelector(
    (state) => state.profile
  );

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    dispatch(fetchQualifications());
  }, [dispatch]);

  const onFinish = async (values) => {
    const payload = {
      qualification: values.qualification,
      institutionName: values.institutionName,
      yearOfQualification: values.yearOfQualification,
    };

    const res = editingId
      ? await dispatch(updateQualification({ id: editingId, data: payload }))
      : await dispatch(addQualification(payload));

    if (res?.payload?.success) {
      showToastSuccess(
        editingId ? "Qualification updated successfully" : "Qualification added successfully",
        closeModal
      );
      form.resetFields();
      setShowForm(false);
      setEditingId(null);
      dispatch(fetchQualifications());
    } else {
      showToastError(
        res?.payload?.message || "Failed to save qualification",
        closeModal
      );
    }
  };

  function handleEdit(item) {
    setEditingId(item.id);
    setShowForm(true);
    form.setFieldsValue({
      qualification: item.qualification,
      institutionName: item.institutionName,
      yearOfQualification: item.yearOfQualification,
    });
  }

  async function handleDelete(id) {
    const res = await dispatch(deleteQualification(id));
    if (res?.payload?.success) {
      showToastSuccess("Qualification deleted successfully", closeModal);
      dispatch(fetchQualifications());
    } else {
      showToastError(
        res?.payload?.message || "Failed to delete qualification",
        closeModal
      );
    }
  }

  function handleGoNext() {
    dispatch(handleNextStep());
  }

  function handleGoBack() {
    dispatch(handlePrevStep());
  }

  function handleCancel() {
    form.resetFields();
    setShowForm(false);
    setEditingId(null);
  }

  if (profileLoading && !qualifications.length && !showForm) {
    return (
      <div className="flex items-center justify-center mt-20">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="mt-10">
      <h3 className="edit-step-head">Professional Qualifications</h3>
      <p className="edit-step-desc mt-2">
        List professional qualifications you've attained below
      </p>

      <div className="flex justify-end w-full">
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="bg-transparent flex items-center border-none"
          >
            <div className="bg-darkBlue rounded-full p-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M4 16C4 15.4477 4.44772 15 5 15H27C27.5523 15 28 15.4477 28 16C28 16.5523 27.5523 17 27 17H5C4.44772 17 4 16.5523 4 16Z"
                  fill="white"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M16 4C16.5523 4 17 4.44772 17 5V27C17 27.5523 16.5523 28 16 28C15.4477 28 15 27.5523 15 27V5C15 4.44772 15.4477 4 16 4Z"
                  fill="white"
                />
              </svg>
            </div>
            <span className="text-darkBlue font-medium text-[17px] ml-3">
              Add Qualification
            </span>
          </button>
        )}
      </div>

      {/* Existing qualifications list */}
      {qualifications.length > 0 && !showForm && (
        <>
          {qualifications.map((item) => (
            <div key={item.id} className="bg-white p-6 mb-4 mt-4 rounded-lg">
              <div className="w-full flex items-start justify-between gap-4">
                <div className="flex flex-col">
                  <h3 className="text-darkBlue text-[21px] font-bold">
                    {item.qualification}
                  </h3>
                  <p className="text-black333 font-medium text-[18px] my-1">
                    {item.institutionName}
                  </p>
                  <span className="text-black333 font-normal text-[17px]">
                    Year: {item.yearOfQualification}
                  </span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => handleEdit(item)}
                    className="text-darkBlue border border-darkBlue rounded-full px-4 py-1 text-sm font-medium hover:bg-blue-50"
                  >
                    Edit
                  </button>
                  <Popconfirm
                    title="Delete this qualification?"
                    okText="Delete"
                    cancelText="Cancel"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => handleDelete(item.id)}
                  >
                    <button
                      type="button"
                      className="text-red-500 border border-red-500 rounded-full px-4 py-1 text-sm font-medium hover:bg-red-50"
                    >
                      Delete
                    </button>
                  </Popconfirm>
                </div>
              </div>
            </div>
          ))}

          <div className="flex flex-col sm:flex-row items-center justify-center mt-10 mb-14 w-full">
            <button
              onClick={handleGoBack}
              className="bg-white rounded-[35px] text-darkBlue border-2 border-darkBlue w-full sm:w-[300px] p-2 mb-2 sm:mb-0 sm:mr-3"
              type="button"
            >
              Back
            </button>
            <button
              onClick={handleGoNext}
              className="bg-darkBlue rounded-[35px] text-white w-full sm:w-[300px] p-3"
              type="button"
            >
              Next
            </button>
          </div>
        </>
      )}

      {/* Empty state */}
      {!qualifications.length && !showForm && (
        <>
          <div className="flex items-center justify-center flex-col">
            <img
              className="object-contain w-full max-w-[550px] h-auto"
              src={eduImg}
              alt="qualification"
            />
            <p className="edit-step-desc my-10">
              Don't have professional qualifications? You can skip this part
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center mt-10 mb-14 w-full">
            <button
              onClick={handleGoBack}
              className="bg-white rounded-[35px] text-darkBlue border-2 border-darkBlue w-full sm:w-[300px] p-2 mb-2 sm:mb-0 sm:mr-3"
              type="button"
            >
              Back
            </button>
            <button
              onClick={handleGoNext}
              className="bg-darkBlue rounded-[35px] text-white w-full sm:w-[300px] p-3"
              type="button"
            >
              Next
            </button>
          </div>
        </>
      )}

      {/* Add/Edit qualification form */}
      {showForm && (
        <Form
          layout="vertical"
          ref={formRef}
          name="add-qualification"
          onFinish={onFinish}
          style={{ maxWidth: "100%", marginTop: 20 }}
          form={form}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            <Form.Item
              name="qualification"
              label="Qualification"
              rules={[
                { required: true, message: "Qualification is required" },
              ]}
            >
              <Input
                className="rounded-[8px] h-[44px] w-full border border-black"
                placeholder="e.g. Bachelors of Education"
              />
            </Form.Item>

            <Form.Item
              name="institutionName"
              label="Name of Institution"
              rules={[
                { required: true, message: "Institution name is required" },
              ]}
            >
              <Input
                className="rounded-[8px] h-[44px] w-full border border-black"
                placeholder="e.g. TUK"
              />
            </Form.Item>

            <Form.Item
              name="yearOfQualification"
              label="Year of Qualification"
              rules={[{ required: true, message: "Year is required" }]}
            >
              <InputNumber
                className="rounded-[8px] h-[44px] w-full border border-black"
                placeholder="e.g. 2020"
                min={1950}
                max={new Date().getFullYear()}
                style={{ width: "100%" }}
              />
            </Form.Item>

          </div>

          <div className="flex flex-col sm:flex-row items-center justify-end mt-10 mb-14 w-full">
            <button
              onClick={handleCancel}
              className="bg-white rounded-[35px] text-darkBlue border-2 border-darkBlue w-full sm:w-[300px] p-2 mb-2 sm:mb-0 sm:mr-3"
              type="button"
            >
              Cancel
            </button>
            <button
              className="bg-darkBlue rounded-[35px] text-white w-full sm:w-[300px] p-3"
              type="submit"
              disabled={profileLoading}
            >
              {profileLoading ? <Spin size="small" /> : editingId ? "Update" : "Save"}
            </button>
          </div>
        </Form>
      )}
    </div>
  );
}
